import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { useLocalStorageValue } from '@react-hookz/web';
import {
  ArrowsClockwiseIcon,
  DotsThreeVerticalIcon,
  EnvelopeIcon,
} from '@phosphor-icons/react';
import {
  Sheet,
  SheetContentResizable,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Tabs } from '@/components/ui/tabs';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { SystemEntity } from '@erp/shared-types';
import {
  EntityDetailTabsProvider,
  EntityDetailTabsHeader,
  EntityDetailTabsContent,
  useEntityDetailTabsContext,
} from '@/components/system-entities/entity-detail-tabs';
import { useArInvoice } from '@/api/rpc/hooks/accounts-receivable/useArInvoices';
import { useRefreshArInvoiceFromFamous } from '@/api/rpc/hooks/accounts-receivable/useRefreshArInvoiceFromFamous';
import { getInvoiceStatusBadge } from '@/components/accounts-receivable/invoice-status-badge';
import { SendInvoiceDialog } from './SendInvoiceDialog';

interface ARInvoiceDetailSlideinProps {
  invoiceId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Inner wrapper rendered within the EntityDetailTabsProvider. Lays out the
 * invoice title + status + actions, then the related-entity tab bar, then the
 * active tab's content (the invoice itself plus any linked sales order,
 * shipment, payments, and email threads).
 */
function ARInvoiceDetailTabs({
  invoiceId,
  onSend,
}: {
  invoiceId: string;
  onSend: () => void;
}) {
  const gt = useGT();
  const { activeTabId, setActiveTabId } = useEntityDetailTabsContext();
  const { invoice, isLoading } = useArInvoice(invoiceId);
  const refreshFromFamous = useRefreshArInvoiceFromFamous();
  const isFamousInvoice = invoice?.originSystem === 'famous';

  return (
    <Tabs
      value={activeTabId}
      onValueChange={setActiveTabId}
      className="flex flex-col flex-1 min-h-0"
    >
      <SheetHeader className="shrink-0 grow-0">
        <div className="flex items-center gap-2">
          {isLoading ? (
            <SheetTitle>
              <T context="loading state title">Loading...</T>
            </SheetTitle>
          ) : invoice ? (
            <>
              <SheetTitle>
                {invoice.invoiceNumber ??
                  gt('(No invoice number)', {
                    context: 'placeholder when invoice has no number',
                  })}
              </SheetTitle>
              {getInvoiceStatusBadge(invoice.status)}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 ml-auto"
                    aria-label={gt('Invoice actions', {
                      context: 'button label to open invoice action menu',
                    })}
                  >
                    <DotsThreeVerticalIcon className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={onSend}>
                    <EnvelopeIcon className="h-4 w-4 mr-2" />
                    <T context="menu item to send invoice email">
                      Send Invoice
                    </T>
                  </DropdownMenuItem>
                  {isFamousInvoice && (
                    <DropdownMenuItem
                      onClick={() =>
                        refreshFromFamous.mutate({ id: invoiceId })
                      }
                      disabled={refreshFromFamous.isPending}
                    >
                      <ArrowsClockwiseIcon
                        className={`h-4 w-4 mr-2 ${refreshFromFamous.isPending ? 'animate-spin' : ''}`}
                      />
                      <T context="menu item to refresh invoice data from Famous ERP">
                        Refresh from Famous
                      </T>
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <SheetTitle>
              <T>Invoice not found</T>
            </SheetTitle>
          )}
        </div>
        <EntityDetailTabsHeader />
      </SheetHeader>
      <div className="flex-1 min-h-0 overflow-hidden">
        <EntityDetailTabsContent />
      </div>
    </Tabs>
  );
}

export function ARInvoiceDetailSlidein({
  invoiceId,
  isOpen,
  onOpenChange,
}: ARInvoiceDetailSlideinProps) {
  // NOTE: currently unused (kept via '_' prefix) — revisit and delete if not needed
  const _gt = useGT();
  const { value: detailViewSize, set: setDetailViewSize } =
    useLocalStorageValue<number>('ar-invoice-detail-view-size', {
      defaultValue: 50,
    });

  const { invoice } = useArInvoice(invoiceId);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContentResizable
        resizableProps={{
          defaultSize: detailViewSize,
          onResize: setDetailViewSize,
          className: 'flex flex-col',
        }}
      >
        {invoiceId ? (
          <EntityDetailTabsProvider
            initialEntity={{
              entityType: SystemEntity.AR_INVOICE,
              entityId: invoiceId,
            }}
          >
            <ARInvoiceDetailTabs
              invoiceId={invoiceId}
              onSend={() => setSendDialogOpen(true)}
            />
          </EntityDetailTabsProvider>
        ) : (
          <>
            <SheetHeader className="shrink-0 grow-0">
              <SheetTitle>
                <T context="title for the AR invoice detail slide-in panel">
                  Invoice Details
                </T>
              </SheetTitle>
            </SheetHeader>
            <div className="p-4 text-sm text-muted-foreground">
              <T context="placeholder text shown when no invoice is selected">
                No invoice selected
              </T>
            </div>
          </>
        )}

        <SheetFooter className="shrink-0 grow-0 flex items-end border-t">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="w-fit"
          >
            <T context="button label to close invoice detail panel">Close</T>
          </Button>
        </SheetFooter>
      </SheetContentResizable>

      {invoice && (
        <SendInvoiceDialog
          open={sendDialogOpen}
          onOpenChange={setSendDialogOpen}
          invoices={[
            {
              invoiceId,
              customerId: invoice.customerId,
              customerName: invoice.customerName,
              invoiceNumber: invoice.invoiceNumber,
            },
          ]}
        />
      )}
    </Sheet>
  );
}
